import { useEffect, useState, useRef } from "react";
import api from "@/lib/api";
import AppLayout from "@/components/AppLayout";
import { toast } from "sonner";
import { Quote, Plus, Check, Trash2, HeartHandshake, Loader2, Sunrise, Timer } from "lucide-react";

const FAITH_IMG = "/faith.webp";

export default function Faith() {
  const [faith, setFaith] = useState(null);
  const [journal, setJournal] = useState("");
  const [prayers, setPrayers] = useState([]);
  const [newPrayer, setNewPrayer] = useState("");
  const [savingJournal, setSavingJournal] = useState(false);
  const [gratitude, setGratitude] = useState(["", "", ""]);
  const [savingGrat, setSavingGrat] = useState(false);
  const [countdown, setCountdown] = useState(null);
  const timerRef = useRef(null);

  const loadPrayers = () => api.get("/prayers").then((r) => setPrayers(r.data)).catch(() => {});

  useEffect(() => {
    api.get("/faith/today").then((r) => {
      setFaith(r.data);
      setJournal(r.data.journal || "");
      const g = r.data.gratitude || [];
      setGratitude([g[0] || "", g[1] || "", g[2] || ""]);
    }).catch(() => {});
    loadPrayers();
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
  }, []);

  const startTimer = () => {
    if (timerRef.current) clearInterval(timerRef.current);
    setCountdown(40);
    timerRef.current = setInterval(() => {
      setCountdown((c) => {
        if (c <= 1) { clearInterval(timerRef.current); timerRef.current = null; return 0; }
        return c - 1;
      });
    }, 1000);
  };

  const saveGratitude = async () => {
    setSavingGrat(true);
    try {
      const { data } = await api.post("/faith/gratitude", { items: gratitude });
      const g = data.items || [];
      setGratitude([g[0] || "", g[1] || "", g[2] || ""]);
      toast.success("Gratitude captured. What a beautiful way to begin. 🌅");
    } catch { toast.error("Could not save gratitude"); }
    finally { setSavingGrat(false); }
  };

  const saveJournal = async () => {
    setSavingJournal(true);
    try {
      await api.post("/faith/journal", { text: journal });
      toast.success("Your reflection is saved.");
    } catch { toast.error("Could not save reflection"); }
    finally { setSavingJournal(false); }
  };

  const addPrayer = async (e) => {
    e.preventDefault();
    if (!newPrayer.trim()) return;
    try {
      const { data } = await api.post("/prayers", { text: newPrayer });
      setPrayers([data, ...prayers]);
      setNewPrayer("");
    } catch { toast.error("Could not add prayer"); }
  };

  const togglePrayer = async (id) => {
    try {
      const { data } = await api.put(`/prayers/${id}/toggle`);
      setPrayers((prev) => prev.map((p) => (p.id === id ? { ...p, answered: data.answered } : p)));
      if (data.answered) toast.success("Praise! Marked as answered. 🙏");
    } catch { toast.error("Could not update prayer"); }
  };

  const deletePrayer = async (id) => {
    try { await api.delete(`/prayers/${id}`); setPrayers((prev) => prev.filter((p) => p.id !== id)); }
    catch { toast.error("Could not remove prayer"); }
  };

  return (
    <AppLayout>
      <div className="max-w-2xl mx-auto">
        <div className="text-center animate-fade-up">
          <span className="text-xs font-medium uppercase tracking-[0.2em] text-[#9B7E8D]">Faith & Encouragement</span>
          <h1 className="mt-2 font-serif text-5xl tracking-tight text-wine">A word for today</h1>
        </div>

        {/* Scripture card */}
        <div
          className="mt-10 relative overflow-hidden rounded-[2rem] border border-taupe text-center p-10 sm:p-14"
          style={{ backgroundImage: `linear-gradient(rgba(250,247,242,0.92), rgba(250,247,242,0.96)), url(${FAITH_IMG})`, backgroundSize: "cover", backgroundPosition: "center" }}
        >
          <Quote className="h-8 w-8 text-gold mx-auto mb-6" strokeWidth={1.5} />
          {faith?.verse && (
            <>
              <p data-testid="scripture-text" className="font-serif italic text-3xl sm:text-4xl text-[#3A1230] leading-snug">
                "{faith.verse.text}"
              </p>
              <p className="mt-6 text-sm uppercase tracking-[0.2em] text-wine">{faith.verse.ref}</p>
            </>
          )}
        </div>

        {/* Encouragement */}
        {faith?.encouragement && (
          <div className="mt-6 bg-wine rounded-3xl p-8 text-center">
            <HeartHandshake className="h-6 w-6 text-white/80 mx-auto mb-3" strokeWidth={1.5} />
            <p data-testid="encouragement-text" className="font-serif text-2xl text-white leading-snug">{faith.encouragement}</p>
          </div>
        )}

        {/* Gratitude Mornings */}
        <div data-testid="gratitude-card" className="mt-8 bg-[#F7E9EF] rounded-3xl p-6 sm:p-8 border border-gold/20">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div className="flex items-center gap-3">
              <div className="h-12 w-12 rounded-2xl bg-white flex items-center justify-center"><Sunrise className="h-6 w-6 text-gold" strokeWidth={1.5} /></div>
              <div>
                <h3 className="font-serif text-2xl text-[#3A1230]">Gratitude Mornings</h3>
                <p className="text-sm text-[#6E5862]">Your first 40 seconds awake — name what you're grateful for.</p>
              </div>
            </div>
            <button data-testid="gratitude-timer-btn" onClick={startTimer}
              className="inline-flex items-center gap-2 rounded-full bg-wine px-5 py-2.5 text-sm font-medium text-white hover:bg-wine-dark transition-all">
              <Timer className="h-4 w-4" strokeWidth={1.5} />
              {countdown != null && countdown > 0 ? `${countdown}s — breathe & give thanks` : "Start 40 seconds"}
            </button>
          </div>

          <div className="mt-5 space-y-3">
            {gratitude.map((g, i) => (
              <div key={i} className="flex items-center gap-3">
                <span className="h-7 w-7 rounded-full bg-white flex items-center justify-center text-wine font-serif text-sm flex-shrink-0">{i + 1}</span>
                <input data-testid={`gratitude-input-${i}`} value={g}
                  onChange={(e) => { const n = [...gratitude]; n[i] = e.target.value; setGratitude(n); }}
                  placeholder={i === 0 ? "I'm grateful for…" : "And also for…"}
                  className="flex-1 rounded-full border border-[#D8C2CD] bg-white px-5 py-3 text-base text-[#3A1230] placeholder-[#9B7E8D] focus:border-wine focus:outline-none focus:ring-1 focus:ring-wine" />
              </div>
            ))}
          </div>
          <button data-testid="gratitude-save" onClick={saveGratitude} disabled={savingGrat}
            className="mt-4 inline-flex items-center gap-2 rounded-full border border-wine px-6 py-3 text-sm font-medium text-wine hover:bg-wine/5 disabled:opacity-60 transition-all">
            {savingGrat ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Check className="h-4 w-4" /> Save gratitude</>}
          </button>
        </div>
        <div className="mt-8 bg-white rounded-3xl p-6 border border-taupe">
          <h3 className="font-serif text-2xl text-[#3A1230] mb-4">Reflect & pray</h3>
          <textarea
            data-testid="faith-journal-input"
            value={journal}
            onChange={(e) => setJournal(e.target.value)}
            rows={4}
            placeholder="Write your prayer, gratitude, or intention for today…"
            className="w-full rounded-2xl border border-[#D8C2CD] bg-cream px-4 py-3 text-base text-[#3A1230] placeholder-[#9B7E8D] focus:border-wine focus:outline-none focus:ring-1 focus:ring-wine resize-none"
          />
          <button
            data-testid="faith-journal-save"
            onClick={saveJournal}
            disabled={savingJournal}
            className="mt-4 inline-flex items-center gap-2 rounded-full bg-wine px-6 py-3 text-sm font-medium text-white transition-all hover:bg-wine-dark disabled:opacity-60"
          >
            {savingJournal ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save reflection"}
          </button>
        </div>

        {/* Prayer list */}
        <div className="mt-8 bg-white rounded-3xl p-6 border border-taupe">
          <h3 className="font-serif text-2xl text-[#3A1230] mb-1">Your prayer list</h3>
          <p className="text-sm text-[#9B7E8D] mb-4">Hold them here, and mark them answered in His timing.</p>
          <form onSubmit={addPrayer} className="flex gap-2">
            <input
              data-testid="prayer-input"
              value={newPrayer}
              onChange={(e) => setNewPrayer(e.target.value)}
              placeholder="Add a prayer or intention…"
              className="flex-1 rounded-full border border-[#D8C2CD] bg-cream px-5 py-3 text-base text-[#3A1230] placeholder-[#9B7E8D] focus:border-wine focus:outline-none focus:ring-1 focus:ring-wine"
            />
            <button data-testid="prayer-add-btn" type="submit" className="rounded-full bg-wine px-5 text-white hover:bg-wine-dark transition-colors">
              <Plus className="h-5 w-5" />
            </button>
          </form>
          <div className="mt-5 space-y-2.5">
            {prayers.length === 0 && <p className="text-sm text-[#9B7E8D] text-center py-6">No prayers yet. Add your first above.</p>}
            {prayers.map((p) => (
              <div key={p.id} data-testid="prayer-item" className="flex items-center gap-3 rounded-2xl bg-cream px-4 py-3 border border-taupe">
                <button
                  data-testid={`prayer-toggle-${p.id}`}
                  onClick={() => togglePrayer(p.id)}
                  className={`h-6 w-6 rounded-full flex items-center justify-center flex-shrink-0 transition-colors ${p.answered ? "bg-[#4A6B53]" : "border-2 border-[#D8C2CD]"}`}
                >
                  {p.answered && <Check className="h-3.5 w-3.5 text-white" strokeWidth={3} />}
                </button>
                <span className={`flex-1 text-base ${p.answered ? "line-through text-[#9B7E8D]" : "text-[#3A1230]"}`}>{p.text}</span>
                {p.answered && <span className="text-xs text-[#4A6B53] font-medium">Answered</span>}
                <button data-testid={`prayer-delete-${p.id}`} onClick={() => deletePrayer(p.id)} className="text-[#9B7E8D] hover:text-[#9B4444] transition-colors">
                  <Trash2 className="h-4 w-4" strokeWidth={1.5} />
                </button>
              </div>
            ))}
          </div>
        </div>
      </div>
    </AppLayout>
  );
}
